import React, {
    Component,
    SyntheticEvent,
    ComponentType,
    ReactNode,
} from 'react';
import {injectIntl, IntlShape} from 'react-intl';
import classNames from 'classnames';
import {petsInFilters} from '@homeaway/search-url-parser';
import {TrackingContext} from '@homeaway/react-event-tracking';
import BedroomsFilter from '../BedroomsFilter/BedroomsFilter';
import BathroomsFilter from '../BathroomsFilter/BathroomsFilter';
import MoreFilters from '../MoreFilters/MoreFilters';
import InstantConfirmationFilter from '../InstantConfirmationFilter/InstantConfirmationFilter';
import PriceFilter, {BoundsType} from '../PriceFilter/PriceFilter';
import seeResultsMessage from '../SeeResultsMessage/SeeResultsMessage';
import {triggerEdapEventForCheckbox, GENERIC_EVENT} from '../../utils/edap';
import {MIN_ALLOWED_WINDOW_HEIGHT_FOR_MODAL_FIXED_CTA} from '../../utils/constants';
import {FilterGroupType} from '../MoreFilters/MoreFilters';
import {FiltersProps, FilterDispatchProps} from '../FilterBar/FilterBar';
import {
    instantBookFilter,
    newArrayFromFilters,
    cannotFilter,
    getFreeCancellationFilters,
} from '../../utils/filters';

import './FilterForm.less';
import FreeCancellationFilter from '../FreeCancellationFilter/FreeCancellationFilter';
import {Refinement} from '../FilterGroup/FilterGroup';

type SearchParamsType = {
    maxBathrooms: number | null;
    maxBedrooms: number | null;
    maxNightlyPrice: number | null;
    minNightlyPrice: number | null;
    maxSleeps?: number;
    minBathrooms?: number;
    minBedrooms?: number;
    filter: any;
};

type Modal = {
    /* place any template in children that might need a 'submit' and/or a 'reset' experience */
    children: ReactNode[];
    isBodyCTA?: boolean;
    headingLabel: string;
    isOpen: boolean;
    modalId: string;
    /* controls whether the reset button is visible */
    resetVisible?: boolean;
    submitDisabled?: boolean;
    submitLabel: string | Node;
    onOpen?: Function;
    onClose: Function;
    onSubmit: Function;
    onReset?: (event?: MouseEvent) => void;
};

export type FilterFormProps = {
    filterGroups: FilterGroupType[];
    inputParams: SearchParamsType;
    modalIsOpen: boolean;
    resultsCount: number | null;
    viewportHeight: number;
    modal: ComponentType<Modal>;
    updateMultipleFilterCheck: (updatedFilters: Refinement[]) => void;
} & FiltersProps &
    FilterDispatchProps;

type Props = {intl: IntlShape} & FilterFormProps;

// TODO: move handlers for shared logic between FilterForm and FilterBar
// https://jira.homeawaycorp.com/browse/SERP-909
export class FilterForm extends Component<Props> {
    componentDidUpdate = (prevProps: Props) => {
        if (this.props.modalIsOpen && !prevProps.modalIsOpen) {
            this.edapEvent('filters open');
        }
    };

    edapEvent = (event: string) => {
        this.context.trigger(GENERIC_EVENT, {
            eventaction: event,
        });
    };

    handleResetForm = () => {
        this.edapEvent('all filters cleared');
        this.props.resetInputParams();
    };

    handleDiscardChanges = () => {
        const {syncInputParams} = this.props;
        syncInputParams();
    };

    handleClose = () => {
        this.edapEvent('filters canceled');
        this.handleDiscardChanges();
        this.props.onDone();
    };

    handleSubmit = () => {
        const {filter, inputParams, onDone, inputParamsAreChanged} = this.props;

        if (inputParamsAreChanged) {
            // sync pets based on pet filters setting
            const petsApplied = petsInFilters(newArrayFromFilters(inputParams));
            this.edapEvent('filters applied');
            filter({
                ...inputParams,
                petIncluded: petsApplied,
            });
        }

        onDone();
    };

    handlePriceChange = (bounds: BoundsType) => {
        const {inputParams, updateInputParams} = this.props;
        const values = {
            ...inputParams,
            minNightlyPrice: bounds[0] || 0,
            maxNightlyPrice: bounds[1],
        };
        updateInputParams(values);
    };

    handleRangeFilterChange = (params: SearchParamsType) => {
        const {updateInputParams} = this.props;
        const update = {
            ...params,
        };
        updateInputParams(update);
    };

    handleMoreFilterCheck = (event: SyntheticEvent<HTMLInputElement>) => {
        if (!event || !event.currentTarget) {
            return;
        }

        triggerEdapEventForCheckbox(event, this.context);

        this.props.updateFilterCheck(event.currentTarget);
    };

    /* eslint-disable complexity */
    render() {
        const {
            handleClose,
            handleResetForm,
            handleSubmit,
            handlePriceChange,
            handleRangeFilterChange,
            handleMoreFilterCheck,
            props: {
                currency,
                filterGroups,
                hasDates,
                inputParams,
                inputParamsAreDefault,
                intl,
                isMobile,
                modalIsOpen,
                priceInputValuesAreValid,
                updatePriceInputValuesValidity,
                updateMultipleFilterCheck,
                resultsCount,
                viewportHeight,
                modal: Modal,
            },
        } = this;
        const isInstantConfirmationChecked = !!instantBookFilter(
            inputParams.filter
        );

        const isBodyCTA: boolean =
            viewportHeight < MIN_ALLOWED_WINDOW_HEIGHT_FOR_MODAL_FIXED_CTA;

        const submitDisabled =
            cannotFilter(resultsCount) || !priceInputValuesAreValid;

        return (
            <div className="FilterForm" data-wdio="FilterForm">
                <Modal
                    modalId="filterFormModal"
                    isOpen={modalIsOpen}
                    submitDisabled={submitDisabled}
                    headingLabel={intl.formatMessage({
                        id: 'filtersDialogLabel',
                        defaultMessage: 'Filters',
                    })}
                    submitLabel={seeResultsMessage({intl, resultsCount})}
                    onClose={handleClose}
                    onReset={handleResetForm}
                    onSubmit={handleSubmit}
                    resetVisible={
                        !inputParamsAreDefault || !priceInputValuesAreValid
                    }
                    isBodyCTA={isBodyCTA}
                >
                    {filterGroups && (
                        <FreeCancellationFilter
                            className="FilterForm_CancellationPolicies"
                            selectedFilters={inputParams.filter}
                            filters={getFreeCancellationFilters(filterGroups)}
                            onMultipleFilterToggle={updateMultipleFilterCheck}
                            oldExperience
                        />
                    )}
                    <PriceFilter
                        currency={currency}
                        hasDates={hasDates}
                        onPriceChange={handlePriceChange}
                        minPrice={inputParams.minNightlyPrice}
                        maxPrice={inputParams.maxNightlyPrice}
                        className="FilterForm__item"
                        priceInputValuesAreValid={priceInputValuesAreValid}
                        updatePriceInputValuesValidity={
                            updatePriceInputValuesValidity
                        }
                    />
                    <BedroomsFilter
                        onChange={handleRangeFilterChange}
                        minBedrooms={inputParams.minBedrooms || 0}
                        maxBedrooms={inputParams.maxBedrooms}
                        className="FilterForm__item"
                    />
                    <div
                        className={classNames(
                            'FilterForm__filter-section--bottom-bordered',
                            'FilterForm__filter-section'
                        )}
                    >
                        <BathroomsFilter
                            onChange={handleRangeFilterChange}
                            minBathrooms={inputParams.minBathrooms || 0}
                            maxBathrooms={inputParams.maxBathrooms}
                        />
                    </div>
                    <InstantConfirmationFilter
                        id="instantConfirmation__switch"
                        checked={isInstantConfirmationChecked}
                        handleChecked={handleMoreFilterCheck}
                        intl={intl}
                        className="FilterForm__item"
                    />
                    {filterGroups && (
                        <MoreFilters
                            checked={newArrayFromFilters(inputParams)}
                            handleChecked={handleMoreFilterCheck}
                            filterGroups={filterGroups}
                            tooltipOptions={{isMobile}}
                        />
                    )}
                </Modal>
            </div>
        );
    }
}

FilterForm.contextType = TrackingContext;

export default injectIntl(FilterForm);
